import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { RequireExactlyOne, TranslateFunc } from 'common/types';
import { isKey } from '../common/utils/KitCommon';
import colors from '../common/theme/colors';
import KitSROnly from './KitSROnly';

export declare type LinkDecoration = 'None' | 'Underline';

const StyledLink = styled.a<{ variant: string; disabled: boolean }>`
  cursor: pointer;
  text-decoration: ${({ variant }) => (variant === 'None' ? 'none' : 'underline')};
  ${({ disabled }) =>
    disabled &&
    `
    color: ${colors.gray_50} !important;
    cursor: initial;
    pointer-events: none;
    `}
  &[aria-disabled='true'] {
    pointer-events: none;
    cursor: initial;
    color: ${colors.gray_50} !important;
  }
`;

const StyledRouterLink = styled(Link)<{ variant: string; disabled: boolean }>`
  cursor: pointer;
  text-decoration: ${({ variant }) => (variant === 'None' ? 'none' : 'underline')};
  ${({ disabled }) =>
    disabled &&
    `
    color: ${colors.gray_50} !important;
    cursor: initial;
    pointer-events: none;
    `}
  &[aria-disabled='true'] {
    pointer-events: none;
    cursor: initial;
    color: ${colors.gray_50} !important;
  }
`;

export interface KitLinkProps extends React.ComponentPropsWithRef<'a'> {
  variant?: LinkDecoration;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  forceAriaDisabled?: boolean;
  onClick?: (event?: React.MouseEvent) => void;
  href?: string;
  to?: string;
  target?: string;
  t: TranslateFunc;
}

const KitLink: React.FC<RequireExactlyOne<KitLinkProps, 'to' | 'href'>> = (props) => {
  const {
    children,
    href,
    to,
    onClick,
    disabled,
    variant = 'None',
    target,
    t,
    ...linkProps
  } = props;

  const onKeyDown = (e) => {
    if (isKey(['ENTER', 'SPACE'], e)) {
      if (!linkProps.forceAriaDisabled) {
        onClick(e);
      }
      e.stopPropagation();
    }
  };

  /*
      Try to use "forceAriaDisabled" most of the time as eventually it'll replace "disabled"
      If disabled={true} then Not Accessible
      If forceAriaDisabled={true} then Accessible
      If both are true, then default to aria-disabled={true} because that means a conscious decision to pass "forceAriaDisabled={true}" was made, meaning it needs to be accessible
      Note: It cannot have "disabled" AND still be accessible. 
    */

  if (to) {
    return (
      <StyledRouterLink
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        to={to}
        disabled={disabled && !linkProps.forceAriaDisabled}
        aria-disabled={linkProps.forceAriaDisabled}
        variant={variant}
        {...linkProps}
      >
        {children}
      </StyledRouterLink>
    );
  }

  return (
    <StyledLink
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={0}
      href={href}
      target={target}
      disabled={disabled && !linkProps.forceAriaDisabled}
      aria-disabled={linkProps.forceAriaDisabled}
      variant={variant}
      {...linkProps}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {children}
      {target === '_blank' && <KitSROnly>({t('kit:link_new_tab')})</KitSROnly>}
    </StyledLink>
  );
};

export default KitLink;
